import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner,
  Accordion, Badge, Table
} from 'react-bootstrap';
import { qscAPI, userAPI } from '../utils/api';

function calcCategoryScore(items, itemValues) {
  let totalFull = 0, totalReceived = 0, deductions = 0;
  for (const item of items) {
    const val = itemValues[item.id] || {};
    const received = parseFloat(val.receivedScore ?? 1);
    totalFull += parseFloat(item.fullScore || 1);
    totalReceived += received;
    if (received === 0) {
      deductions += parseFloat(val.deduction ?? item.criticalPct ?? 0) / 100;
    }
  }
  if (totalFull === 0) return 0;
  return Math.max(0, ((totalReceived / totalFull) - deductions) * 100);
}

const scoreColor = (pct) => {
  if (pct >= 80) return 'success';
  if (pct >= 70) return 'warning';
  return 'danger';
};

const QscNew = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [disabledCategoryIds, setDisabledCategoryIds] = useState([]);
  const [itemValues, setItemValues] = useState({});
  const [itemPhotos, setItemPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadData();
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, usersRes] = await Promise.all([
        qscAPI.getCategories(),
        userAPI.getAll()
      ]);
      const cats = catRes.data.data;
      setCategories(cats);

      // Default all items to pass (receivedScore=1)
      const defaults = {};
      for (const cat of cats) {
        for (const item of cat.items || []) {
          defaults[item.id] = { receivedScore: '1', remarks: '', deduction: item.criticalPct || 0 };
        }
      }
      setItemValues(defaults);

      const branchUsers = usersRes.data.data.filter(u => !u.isSuperuser && !u.isStaff);
      setUsers(branchUsers);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (catId) => {
    setDisabledCategoryIds(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const setScore = (itemId, score) => {
    setItemValues(prev => ({ ...prev, [itemId]: { ...prev[itemId], receivedScore: score } }));
  };

  const setRemarks = (itemId, remarks) => {
    setItemValues(prev => ({ ...prev, [itemId]: { ...prev[itemId], remarks } }));
  };

  const setDeduction = (itemId, deduction) => {
    setItemValues(prev => ({ ...prev, [itemId]: { ...prev[itemId], deduction } }));
  };

  const setItemEvidencePhotos = (itemId, files) => {
    setItemPhotos(prev => {
      (prev[itemId] || []).forEach(f => {
        if (f._objectUrl) URL.revokeObjectURL(f._objectUrl);
      });
      return { ...prev, [itemId]: files };
    });
  };

  const removeItemPhoto = (itemId, index) => {
    setItemPhotos(prev => {
      const updated = (prev[itemId] || []).filter((_, i) => i !== index);
      return { ...prev, [itemId]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) { setError('กรุณาเลือกสาขาที่ต้องการประเมิน'); return; }
    setSubmitting(true);
    setError('');
    try {
      const items = categories.flatMap(cat =>
        (cat.items || []).map(item => {
          const val = itemValues[item.id] || {};
          const receivedScore = parseFloat(val.receivedScore ?? 1);
          return {
            itemId: item.id,
            receivedScore,
            criticalDeduction: receivedScore === 0 ? parseFloat(val.deduction ?? 0) : 0,
            remarks: val.remarks || null
          };
        })
      );
      const createRes = await qscAPI.createAssessment({
        branchId, visitDate, items, notes, disabledCategoryIds
      });
      const assessmentId = createRes.data.data.id;
      await qscAPI.completeAssessment(assessmentId);

      // Upload evidence photos for failed items
      const photoEntries = Object.entries(itemPhotos).filter(([, files]) => files && files.length > 0);
      if (photoEntries.length > 0) {
        await Promise.all(photoEntries.map(([itemId, files]) => {
          const formData = new FormData();
          for (const file of files) formData.append('photos', file);
          return qscAPI.uploadAssessmentItemPhotos(assessmentId, itemId, formData);
        }));
      }

      navigate(`/qsc/${assessmentId}`);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึก');
      setSubmitting(false);
    }
  };

  // Live scores
  const scores = {};
  for (const cat of categories) {
    scores[cat.id] = calcCategoryScore(cat.items || [], itemValues);
  }
  const enabledCats = categories.filter(c => !disabledCategoryIds.includes(c.id));
  const overall = enabledCats.length > 0
    ? enabledCats.reduce((s, c) => s + scores[c.id], 0) / enabledCats.length
    : 0;

  if (loading) {
    return (
      <Container fluid className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">กำลังโหลดแบบประเมิน...</div>
        </div>
      </Container>
    );
  }

  if (categories.length === 0) {
    return (
      <Container fluid className="main-content">
        <Alert variant="warning">
          ยังไม่มีหมวดหมู่การประเมิน กรุณาเพิ่มข้อมูลใน{' '}
          <a href="/qsc/templates">จัดการ Templates QSC</a> ก่อน
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2><i className="bi bi-clipboard-plus me-2"></i>สร้างแบบประเมิน QSC ใหม่</h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            {/* Setup card */}
            <Card className="mb-4">
              <Card.Header className="bg-primary text-white fw-bold">
                <i className="bi bi-gear me-2"></i>ข้อมูลการประเมิน
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>สาขาที่ประเมิน <span className="text-danger">*</span></Form.Label>
                      <Form.Select value={branchId} onChange={e => setBranchId(e.target.value)} required>
                        <option value="">-- เลือกสาขา --</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.fullName || u.username}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>วันที่ตรวจ</Form.Label>
                      <Form.Control
                        type="date" value={visitDate}
                        onChange={e => setVisitDate(e.target.value)} required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>หมายเหตุ (ไม่บังคับ)</Form.Label>
                      <Form.Control
                        as="textarea" rows={2} value={notes}
                        onChange={e => setNotes(e.target.value)} placeholder="หมายเหตุเพิ่มเติม..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

              </Card.Body>
            </Card>

            {/* Checklist accordion */}
            <Accordion defaultActiveKey={String(categories[0]?.id)} alwaysOpen>
              {categories.map(cat => {
                const isDisabled = disabledCategoryIds.includes(cat.id);
                const catScore = scores[cat.id] || 0;

                // Group items by subcategoryId
                const subGroups = {};
                const noSubItems = [];
                for (const item of (cat.items || [])) {
                  if (item.subcategoryId) {
                    if (!subGroups[item.subcategoryId]) subGroups[item.subcategoryId] = [];
                    subGroups[item.subcategoryId].push(item);
                  } else {
                    noSubItems.push(item);
                  }
                }

                // Get subcategory objects for display
                const subMap = {};
                for (const sub of (cat.subcategories || [])) {
                  subMap[sub.id] = sub;
                }

                const renderItemRow = (item) => {
                  const val = itemValues[item.id] || {};
                  const isFail = parseFloat(val.receivedScore) === 0;
                  const isCritical = parseFloat(item.criticalPct) > 0;
                  const evidenceFiles = itemPhotos[item.id] || [];
                  return (
                    <tr key={item.id} className={isFail ? 'table-danger' : ''}>
                      <td>
                        <code>{item.itemCode}</code>
                        {isCritical && (
                          <Badge bg="danger" className="ms-1" style={{ fontSize: '0.65rem' }}>
                            max -{item.criticalPct}%
                          </Badge>
                        )}
                      </td>
                      <td style={{ fontSize: '0.875rem' }}>{item.description}</td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <Button size="sm" variant={!isFail ? 'success' : 'outline-success'}
                            onClick={() => setScore(item.id, '1')} title="ผ่าน">
                            <i className="bi bi-check-lg"></i>
                          </Button>
                          <Button size="sm" variant={isFail ? 'danger' : 'outline-danger'}
                            onClick={() => setScore(item.id, '0')} title="ไม่ผ่าน">
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        </div>
                      </td>
                      <td className="text-center">
                        {isFail && isCritical ? (() => {
                          const max = parseFloat(item.criticalPct);
                          const current = parseInt(val.deduction ?? max) || 1;
                          const adjust = (delta) => {
                            setDeduction(item.id, Math.min(Math.max(1, current + delta), max));
                          };
                          return (
                            <div>
                              <div className="d-flex align-items-center gap-1 justify-content-center">
                                <Button size="sm" variant="outline-secondary" style={{ width: 26, padding: 0 }}
                                  onClick={() => adjust(-1)} disabled={current <= 1}>−</Button>
                                <Form.Control
                                  size="sm" type="number" min={1} max={max} step={1}
                                  value={current}
                                  onChange={e => {
                                    const v = Math.min(Math.max(1, parseInt(e.target.value) || 1), max);
                                    setDeduction(item.id, v);
                                  }}
                                  style={{ width: '52px', textAlign: 'center' }}
                                />
                                <Button size="sm" variant="outline-secondary" style={{ width: 26, padding: 0 }}
                                  onClick={() => adjust(1)} disabled={current >= max}>+</Button>
                                <span className="text-muted small">%</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                1 – {item.criticalPct}%
                              </div>
                            </div>
                          );
                        })() : isFail ? (
                          <span className="text-muted small">-</span>
                        ) : null}
                      </td>
                      <td>
                        {isFail && (
                          <Form.Control size="sm" type="text" placeholder="บันทึก..."
                            value={val.remarks || ''}
                            onChange={e => setRemarks(item.id, e.target.value)}
                          />
                        )}
                      </td>
                      <td>
                        {isFail && (
                          <div>
                            <Form.Control
                              size="sm" type="file" multiple
                              accept="image/jpeg,image/png,image/webp"
                              onChange={e => setItemEvidencePhotos(item.id, Array.from(e.target.files))}
                              style={{ fontSize: '0.75rem' }}
                            />
                            {evidenceFiles.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {evidenceFiles.map((f, i) => (
                                  <div key={i} style={{ position: 'relative', width: 36, height: 36 }}>
                                    <img
                                      src={URL.createObjectURL(f)}
                                      alt="preview"
                                      style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3 }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeItemPhoto(item.id, i)}
                                      style={{
                                        position: 'absolute', top: -4, right: -4,
                                        width: 16, height: 16, padding: 0,
                                        background: '#dc3545', border: 'none',
                                        borderRadius: '50%', color: '#fff',
                                        fontSize: '0.6rem', lineHeight: 1,
                                        cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center'
                                      }}
                                    >✕</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                };

                const tableHeader = (
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '90px' }}>รหัส</th>
                      <th>รายการประเมิน</th>
                      <th style={{ width: '130px' }} className="text-center">ผล</th>
                      <th style={{ width: '160px' }} className="text-center">หักคะแนน</th>
                      <th style={{ width: '180px' }}>หมายเหตุ</th>
                      <th style={{ width: '180px' }}>รูปหลักฐาน</th>
                    </tr>
                  </thead>
                );

                return (
                  <Accordion.Item eventKey={String(cat.id)} key={cat.id} className="mb-3">
                    <Accordion.Header>
                      <Form.Check
                        type="checkbox"
                        id={`cat-toggle-${cat.id}`}
                        checked={!isDisabled}
                        onChange={() => toggleCategory(cat.id)}
                        onClick={e => e.stopPropagation()}
                        className="me-2"
                        title={isDisabled ? 'คลิกเพื่อนับคะแนน' : 'คลิกเพื่อไม่นับคะแนน'}
                      />
                      <span className={`fw-bold me-2 ${isDisabled ? 'text-muted' : ''}`}>
                        {cat.name}
                      </span>
                      <Badge bg={isDisabled ? 'secondary' : scoreColor(catScore)} className="me-2">
                        {catScore.toFixed(1)}%
                      </Badge>
                      <span className="text-muted small">({(cat.items || []).length} รายการ)</span>
                    </Accordion.Header>
                    <Accordion.Body className="p-0">
                      {/* Items with no subcategory */}
                      {noSubItems.length > 0 && (
                        <Table hover className="mb-0" size="sm">
                          {tableHeader}
                          <tbody>{noSubItems.map(renderItemRow)}</tbody>
                        </Table>
                      )}
                      {/* Items grouped by subcategory */}
                      {Object.entries(subGroups).map(([subId, subItems]) => (
                        <div key={subId}>
                          <div className="px-3 py-2 bg-light border-top border-bottom text-muted small fw-semibold">
                            {subMap[subId]?.name || 'หมวดย่อย'}
                          </div>
                          <Table hover className="mb-0" size="sm">
                            {tableHeader}
                            <tbody>{subItems.map(renderItemRow)}</tbody>
                          </Table>
                        </div>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Col>

          {/* Live score sidebar */}
          <Col lg={4}>
            <div style={{ position: 'sticky', top: '80px' }}>
              <Card className="mb-3">
                <Card.Header className="bg-dark text-white fw-bold">
                  <i className="bi bi-bar-chart me-2"></i>คะแนนสรุป (Live)
                </Card.Header>
                <Card.Body>
                  {categories.map(cat => {
                    const isDisabled = disabledCategoryIds.includes(cat.id);
                    const catScore = scores[cat.id] || 0;
                    return (
                      <div key={cat.id} className={`mb-3 ${isDisabled ? 'opacity-50' : ''}`}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-semibold small">
                            {cat.name}
                            {isDisabled && <span className="ms-1 text-muted">(ปิด)</span>}
                          </span>
                          <Badge bg={isDisabled ? 'secondary' : scoreColor(catScore)}>
                            {catScore.toFixed(1)}%
                          </Badge>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div
                            className={`progress-bar bg-${isDisabled ? 'secondary' : scoreColor(catScore)}`}
                            style={{ width: `${Math.min(catScore, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <hr />
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold fs-5">คะแนนรวม</span>
                    <Badge bg={scoreColor(overall)} style={{ fontSize: '1rem', padding: '0.5em 0.75em' }}>
                      {overall.toFixed(1)}%
                    </Badge>
                  </div>
                  {disabledCategoryIds.length > 0 && (
                    <div className="text-muted small mt-1">คำนวณจาก {enabledCats.length} หมวด</div>
                  )}
                  <div className="text-center mt-2">
                    <Badge bg={overall >= 80 ? 'success' : 'danger'} style={{ fontSize: '0.9rem' }}>
                      {overall >= 80 ? 'ผ่าน' : 'ไม่ผ่าน'}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              <div className="d-grid gap-2">
                <Button variant="primary" size="lg" type="submit" disabled={submitting || !branchId}>
                  {submitting ? (
                    <><Spinner size="sm" className="me-2" />กำลังบันทึก...</>
                  ) : (
                    <><i className="bi bi-send-check me-2"></i>บันทึกและส่งรายงาน</>
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={() => navigate('/qsc')} disabled={submitting}>
                  ยกเลิก
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default QscNew;
